import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ForgeDialog } from "@/components/forge/ForgeDialog";
import { ForgeButton } from "@/components/forge/ForgeButton";
import { ForgeSelect } from "@/components/forge/ForgeSelect";
import { Field, ErrorSummary } from "@/components/forge/Field";
import { ErrorState, SkeletonRows } from "@/components/forge/States";
import { Icon } from "@/components/forge/Icon";
import { initialsFrom } from "@/lib/format";
import { DATE_FORMAT_KEYS, TIME_FORMATS } from "@/lib/orgFormats";
import {
  useProfile,
  useTimezones,
  useUpdateProfile,
} from "@/features/auth/mutations";
import { canonicalTimezone } from "@/features/auth/api";
import { FILE_PURPOSES, acceptFor, checkFile, uploadFile } from "@/features/files/api";
import { useToast } from "@/providers/ToastProvider";

/**
 * The signed-in person editing their own record.
 *
 * Reached from the account menu at the foot of the rail — the same menu that
 * previews a role and signs out — because that is where someone looks for
 * themselves. It is deliberately NOT under Settings: everything there governs
 * the organisation, and a screen that changes one person's own password sitting
 * beside one that changes the whole tenant's retention policy is how the wrong
 * one gets edited.
 *
 * ── A dialog, not a route ────────────────────────────────────────────────
 * The product's standing rule is that every drill-down is deep-linkable, and
 * this is the exception the rule allows: there is nothing here to link TO. A
 * profile form is not a place in the plant that one person sends another, it is
 * a thing you do and close, and the menu it opens from is present on every
 * screen already.
 *
 * ── Two writes, one form ─────────────────────────────────────────────────
 * The details and the password go up in the same `PUT`, because the API takes
 * them in the same body. They are still separated visually, and the password
 * pair stays collapsed until asked for: most visits here are to fix a surname or
 * a time zone, and a form that opens with two empty password boxes reads as a
 * form that wants a password.
 */

/**
 * The avatar's rules are the API's, read from `features/files/api.js` rather
 * than restated here. This file used to carry its own 512KB cap, copied from
 * the organisation logo field, which refused images the server would have taken.
 */
const AVATAR = FILE_PURPOSES.userAvatar;

/**
 * Good enough to catch a typo, deliberately not a validator.
 *
 * Email syntax is not something a regex settles, and a strict one rejects real
 * addresses. This asks only whether there is an `@` with something either side,
 * which is what catches the actual mistake — a half-typed address — and leaves
 * the ruling to the server, which is the only party that can make it.
 */
const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export function EditProfileDialog({ open, onOpenChange }) {
  const { t } = useTranslation(["app", "common"]);
  /* The editable row, not the session. Fetched only while the dialog is open —
     `avatar_view_url` on it is a five-minute presigned address, so holding one
     between opens would render a broken image. */
  const profile = useProfile({ enabled: open });
  /* The zones the API will accept — not the browser's list, which is a
     different set and gets refused on save. */
  const zones = useTimezones({ enabled: open });

  return (
    <ForgeDialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t("profile.title")}
      description={t("profile.intro")}
    >
      {/* Mounted only while the dialog is open, which is what seeds the draft:
          the form reads the profile in its `useState` initialisers, so opening
          is the read and nothing has to synchronise afterwards. The consequence
          is the one worth having — a `/auth/me` refetch landing mid-edit cannot
          overwrite what somebody has typed. */}
      {!open ? null : profile.isPending || zones.isPending ? (
        <ProfileLoading />
      ) : profile.isError || zones.isError ? (
        <ProfileUnavailable
          onRetry={() => {
            if (profile.isError) profile.refetch();
            if (zones.isError) zones.refetch();
          }}
        />
      ) : (
        <ProfileForm
          key={profile.data?.id ?? "me"}
          user={profile.data}
          zones={zones.data}
          onDone={() => onOpenChange(false)}
        />
      )}
    </ForgeDialog>
  );
}

/**
 * The record has to arrive before the form can be seeded, and seeding is the
 * whole reason the form mounts on open — so this is a real wait, not a
 * decoration. Rows rather than a spinner, because what is coming is a column of
 * fields and the shape should not change when it lands.
 */
function ProfileLoading() {
  return <SkeletonRows count={5} />;
}

/**
 * Stated rather than silently falling back to an empty form. A blank profile
 * that saves is how somebody clears their own name without meaning to.
 */
function ProfileUnavailable({ onRetry }) {
  const { t } = useTranslation(["app", "common"]);
  return <ErrorState title={t("profile.loadFailed")} onRetry={onRetry} />;
}

function ProfileForm({ user, zones, onDone }) {
  const { t } = useTranslation(["app", "common", "auth"]);
  const save = useUpdateProfile();
  const { toast } = useToast();

  const [draft, setDraft] = useState(() => ({
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    /* Reconciled against the list the API will accept. A record holding a
       deprecated alias seeds a select that cannot show it and a save that will
       be refused — see `canonicalTimezone`. Null when nothing matches, which
       the field states rather than substituting a zone nobody chose. */
    timezone: canonicalTimezone(user?.timezone, zones),
    dateFormat: user?.dateFormat ?? "dmy",
    timeFormat: user?.timeFormat ?? "h24",
    /* Carried so the update can send it back untouched. The PUT is a full
       replace and gives `avatar_file_id` no default, so "leave the picture
       alone" is spelled by resending the id, not by omitting it. */
    currentAvatarFileId: user?.avatarFileId ?? null,
    currentPassword: "",
    newPassword: "",
  }));
  const [errors, setErrors] = useState({});
  const [changingPassword, setChangingPassword] = useState(false);
  const [revealed, setRevealed] = useState(false);
  /**
   * The picked avatar.
   *
   * `preview` is a data URL for the thumbnail and never leaves the browser.
   * `file` is the actual image, held until submit: it is uploaded as part of
   * saving rather than on picking, so somebody who picks a photo and then
   * cancels has not left an orphan in the bucket.
   *
   * `removed` is the third state, and it is not the absence of the other two —
   * it is an explicit instruction to send `avatar_file_id: null` and detach the
   * one already on the record.
   */
  const [avatar, setAvatar] = useState({ preview: null, file: null, removed: false });
  const [uploading, setUploading] = useState(false);

  const summaryRef = useRef(null);
  const picker = useRef(null);


  /* Compared against what the record actually holds, not against "has anyone
     typed here" — retyping the same address is not a change and must not demand
     a password. */
  const emailChanging =
    draft.email.trim().toLowerCase() !== (user?.email ?? "").trim().toLowerCase();

  const set = (key) => (value) =>
    setDraft((previous) => ({ ...previous, [key]: value }));
  const field = (key) => (event) => set(key)(event.target.value);

  /**
   * Checked against the server's own rules before anything is read or sent, so
   * an 8MB photo is refused instantly rather than after 8MB has gone up the
   * wire and come back rejected.
   */
  function pickAvatar(file) {
    if (!file) return;

    const problem = checkFile(file, AVATAR);
    if (problem) {
      toast(t(`profile.avatar.${problem.reason}`, { limit: problem.limit }), "critical");
      return;
    }

    const reader = new FileReader();
    reader.onload = () =>
      setAvatar({ preview: String(reader.result), file, removed: false });
    reader.onerror = () => toast(t("profile.avatar.failed"), "critical");
    reader.readAsDataURL(file);
  }

  function validate() {
    const next = {};
    if (!draft.firstName.trim())
      next.firstName = t("profile.fields.firstNameRequired");
    if (!draft.lastName.trim())
      next.lastName = t("profile.fields.lastNameRequired");
    // Null when the stored zone was not in the API's list and could not be
    // mapped to one. Refused here rather than sent, since the server answers it
    // with a 422 naming a field nobody knowingly touched.
    if (!draft.timezone) next.timezone = t("profile.fields.timezoneRequired");

    if (!draft.email.trim()) next.email = t("auth:fields.email.required");
    else if (!looksLikeEmail(draft.email.trim()))
      next.email = t("auth:fields.email.format");

    // The server requires the current password before it will change an email
    // OR a password, and refuses the request without it. Asked for here rather
    // than discovered as a 400 after everything else has been filled in.
    if (emailChanging && !draft.currentPassword) {
      next.currentPassword = t("profile.password.currentRequiredEmail");
    }

    if (changingPassword) {
      if (!draft.currentPassword)
        next.currentPassword = t("profile.password.currentRequired");
      if (!draft.newPassword)
        next.newPassword = t("profile.password.newRequired");
      // The one rule worth enforcing client-side: it is about the request being
      // coherent, not about password strength, which is the server's to judge.
      else if (draft.newPassword === draft.currentPassword) {
        next.newPassword = t("profile.password.mustDiffer");
      }
    }
    return next;
  }

  /**
   * Upload first, then save.
   *
   * The image goes up as part of submitting rather than on picking, for two
   * reasons. A picked-then-cancelled photo leaves nothing behind in the bucket;
   * and the id it produces is only useful attached to this record, which is the
   * write that follows. If the upload fails the profile is not saved either —
   * saving the name and silently dropping the photo is the outcome that gets
   * reported as "it lost my picture".
   */
  async function submit(event) {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      // Focus the summary, or a keyboard user submits and nothing appears to
      // happen — the errors render below where focus is sitting.
      summaryRef.current?.focus();
      return;
    }

    /**
     * Three states, three values. `undefined` means "leave whatever is on the
     * record"; a string attaches a new file; `null` detaches the current one.
     * They are genuinely different instructions and the API reads them as such.
     */
    let avatarFileId;
    if (avatar.file) {
      setUploading(true);
      try {
        avatarFileId = await uploadFile(avatar.file, AVATAR);
      } catch (error) {
        setUploading(false);
        toast(
          error?.fields?.file === "rejected"
            ? t("profile.avatar.rejected")
            : t("profile.avatar.uploadFailed"),
          "critical",
        );
        return;
      }
      setUploading(false);
    } else if (avatar.removed) {
      avatarFileId = null;
    }

    save.mutate(
      {
        ...draft,
        avatarFileId,
        currentPassword: changingPassword ? draft.currentPassword : undefined,
        newPassword: changingPassword ? draft.newPassword : undefined,
      },
      {
        onSuccess: () => {
          toast(t("profile.saved"), "positive");
          onDone();
        },
      },
    );
  }

  const name = [draft.firstName, draft.lastName].filter(Boolean).join(" ");

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4.5">
      <ErrorSummary
        ref={summaryRef}
        id="profile-errors"
        title={t("profile.errorSummary", { count: Object.keys(errors).length })}
        errors={errors}
      />

      {/* A failed write, stated where it was attempted. The server's own
            wording is never rendered — it is untranslated and can describe
            internals — so this says what happened in the product's language and
            leaves the dialog open with everything still typed in it. */}
      {save.isError ? (
        <div
          role="alert"
          className="rounded-sm border border-hairline stripe-red bg-card p-3.5"
        >
          <p className="m-0 text-control text-on-red">
            {t("profile.saveFailed")}
          </p>
          <p className="mt-1 mb-0 text-body-sm text-ink-dim">
            {/* The server answers a wrong current password or a taken email
                as a 400 with a field-keyed reason, not a 401 — so the field is
                what identifies it. Its own wording is never rendered: it is
                untranslated and can describe internals. */}
            {save.error?.fields?.current_password
              ? t("profile.saveFailedPassword")
              : save.error?.fields?.email
                ? t("profile.saveFailedEmail")
                : t("profile.saveFailedHint")}
          </p>
        </div>
      ) : null}

      <AvatarField
        name={name}
        avatar={avatar}
        currentUrl={user?.avatarUrl ?? null}
        onPick={() => picker.current?.click()}
        onUndo={() => setAvatar({ preview: null, file: null, removed: false })}
        onRemove={() => setAvatar({ preview: null, file: null, removed: true })}
      />
      <input
        ref={picker}
        type="file"
        /* Exactly what the API will take, so the file browser does not offer a
           HEIC or a GIF that only fails once it has been uploaded. */
        accept={acceptFor(AVATAR)}
        className="sr-only"
        aria-label={t("profile.avatar.change")}
        onChange={(event) => {
          pickAvatar(event.target.files?.[0]);
          // Cleared so choosing the same file twice fires again.
          event.target.value = "";
        }}
      />

      <div className="grid gap-3.5 sm:grid-cols-2">
        <Field
          id="firstName"
          label={t("profile.fields.firstName")}
          required
          autoFocus
          autoComplete="given-name"
          value={draft.firstName}
          error={errors.firstName}
          onChange={field("firstName")}
        />
        <Field
          id="lastName"
          label={t("profile.fields.lastName")}
          required
          autoComplete="family-name"
          value={draft.lastName}
          error={errors.lastName}
          onChange={field("lastName")}
        />
      </div>

      <Field
        id="email"
        label={t("auth:fields.email.label")}
        /* Changing it does not take effect on save — the server emails a
           confirmation link to the new address and leaves the record alone
           until it is opened. Said here, because somebody who signs out
           believing the change landed cannot get back in. */
        hint={
          emailChanging
            ? t("profile.fields.emailConfirm")
            : t("profile.fields.emailHint")
        }
        required
        type="email"
        autoComplete="email"
        value={draft.email}
        error={errors.email}
        onChange={field("email")}
      />

      <div className="border-t border-hairline pt-4">
        <p className="eyebrow mb-3">{t("profile.regional.title")}</p>
        <div className="grid gap-3.5 sm:grid-cols-3">
          <ForgeSelect
            id="profile-timezone"
            label={t("profile.fields.timezone")}
            /* Only when the stored zone could not be reconciled: the select
               opens with nothing chosen, and without this it is a blank control
               with no explanation for why. */
            hint={
              draft.timezone
                ? t("profile.fields.timezoneHint")
                : t("profile.fields.timezoneUnknown", {
                    zone: user?.timezone ?? "",
                  })
            }
            value={draft.timezone ?? ""}
            onValueChange={set("timezone")}
            error={errors.timezone}
            /* An IANA identifier is a machine string — the same in every
                 locale — so it is printed rather than translated. */
            options={zones.map((zone) => ({ value: zone, label: zone }))}
          />
          <ForgeSelect
            id="profile-date-format"
            label={t("profile.fields.dateFormat")}
            value={draft.dateFormat}
            onValueChange={set("dateFormat")}
            options={DATE_FORMAT_KEYS.map((key) => ({
              value: key,
              label: t(`settings.general.dateFormats.${key}`),
            }))}
          />
          <ForgeSelect
            id="profile-time-format"
            label={t("profile.fields.timeFormat")}
            value={draft.timeFormat}
            onValueChange={set("timeFormat")}
            options={TIME_FORMATS.map((key) => ({
              value: key,
              label: t(`settings.general.timeFormats.${key}`),
            }))}
          />
        </div>
      </div>

      <PasswordSection
        open={changingPassword}
        /* An email change needs the current password too, so the field appears
           on its own — otherwise validation points at a control that is not on
           screen, and the error summary links to nothing. */
        requireCurrent={emailChanging}
        draft={draft}
        errors={errors}
        revealed={revealed}
        onToggle={() => {
          setChangingPassword((was) => !was);
          setDraft((previous) => ({
            ...previous,
            currentPassword: "",
            newPassword: "",
          }));
          setErrors(
            ({ currentPassword: _c, newPassword: _n, ...rest }) => rest,
          );
        }}
        onReveal={() => setRevealed((was) => !was)}
        onChange={field}
      />

      <div className="flex flex-wrap gap-2 border-t border-hairline pt-4">
        <ForgeButton
          type="submit"
          variant="primary"
          disabled={save.isPending || uploading}
        >
          {uploading
            ? t("profile.avatar.uploading")
            : save.isPending
              ? t("profile.saving")
              : t("common:actions.save")}
        </ForgeButton>
        <ForgeButton type="button" onClick={onDone}>
          {t("common:actions.cancel")}
        </ForgeButton>
      </div>
    </form>
  );
}

/**
 * The avatar, its picker, and what saving would actually do.
 *
 * Nothing here uploads. The picked file is held by the form and goes up as part
 * of submitting, so the thumbnail shows a local preview and the line beneath it
 * says the picture is staged rather than saved — which is the truth until Save
 * is pressed, and the difference somebody would otherwise discover a week later.
 */
function AvatarField({ name, avatar, currentUrl, onPick, onUndo, onRemove }) {
  const { t } = useTranslation(["app", "common"]);

  /* `removed` wins over the stored URL: it is the instruction to detach, and the
     thumbnail has to show what saving would leave behind, not what is still on
     the record until then. */
  const shown = avatar.preview ?? (avatar.removed ? null : currentUrl);
  const pending = Boolean(avatar.file) || avatar.removed;

  return (
    <div className="flex items-start gap-4">
      {/* Clickable, because a picture of yourself is the thing people click. The
          buttons beside it carry the same action for anyone navigating by
          keyboard or reading the labels. */}
      <button
        type="button"
        onClick={onPick}
        className="group relative grid size-18 shrink-0 place-items-center overflow-hidden rounded-full border border-hairline bg-sunken transition-colors hover:border-forge"
        aria-label={t("profile.avatar.change")}
      >
        {shown ? (
          <img src={shown} alt="" className="size-full object-cover" />
        ) : (
          <span className="text-title font-bold text-ink-dim">
            {name ? initialsFrom(name) : <Icon name="users" size="lg" />}
          </span>
        )}
        <span className="absolute inset-0 grid place-items-center bg-ink/0 opacity-0 transition-opacity group-hover:opacity-100">
          <Icon name="pencil" size="sm" className="text-ink" />
        </span>
      </button>

      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="text-control text-ink">
          {t("profile.avatar.label")}
        </span>
        <div className="flex flex-wrap gap-2">
          <ForgeButton size="sm" type="button" onClick={onPick}>
            {t("profile.avatar.change")}
          </ForgeButton>
          {/* Undo while something is staged, remove while there is a picture on
              the record and nothing staged over it. Never both: two buttons that
              differ only in which direction they revert is a coin toss. */}
          {pending ? (
            <ForgeButton size="sm" type="button" variant="ghost" onClick={onUndo}>
              {t("profile.avatar.undo")}
            </ForgeButton>
          ) : currentUrl ? (
            <ForgeButton size="sm" type="button" variant="ghost" onClick={onRemove}>
              {t("profile.avatar.remove")}
            </ForgeButton>
          ) : null}
        </div>
        <p className="m-0 text-micro font-normal text-ink-mute">
          {avatar.file
            ? t("profile.avatar.staged")
            : avatar.removed
              ? t("profile.avatar.willRemove")
              : t("profile.avatar.hint")}
        </p>
      </div>
    </div>
  );
}

/**
 * The password pair, collapsed until asked for.
 *
 * `autoComplete` is spelled out on both: a password manager that cannot tell the
 * old field from the new one will offer to overwrite the stored credential with
 * whatever is in the first box, which is how people lock themselves out.
 */
function PasswordSection({
  open,
  requireCurrent,
  draft,
  errors,
  revealed,
  onToggle,
  onReveal,
  onChange,
}) {
  const { t } = useTranslation(["app", "common", "auth"]);

  return (
    <div className="border-t border-hairline pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="eyebrow mb-0.5">{t("profile.password.title")}</p>
          <p className="m-0 text-body-sm text-ink-dim">
            {t("profile.password.intro")}
          </p>
        </div>
        <ForgeButton size="sm" type="button" onClick={onToggle}>
          {open ? t("common:actions.cancel") : t("profile.password.change")}
        </ForgeButton>
      </div>

      {requireCurrent && !open ? (
        <div className="mt-3.5 sm:max-w-1/2">
          <Field
            id="currentPassword"
            label={t("profile.password.current")}
            hint={t("profile.password.currentForEmail")}
            required
            type={revealed ? "text" : "password"}
            autoComplete="current-password"
            value={draft.currentPassword}
            error={errors.currentPassword}
            onChange={onChange("currentPassword")}
          />
        </div>
      ) : null}

      {open ? (
        <div className="mt-3.5 grid gap-3.5 sm:grid-cols-2">
          <Field
            id="currentPassword"
            label={t("profile.password.current")}
            required
            type={revealed ? "text" : "password"}
            autoComplete="current-password"
            value={draft.currentPassword}
            error={errors.currentPassword}
            onChange={onChange("currentPassword")}
          />
          <Field
            id="newPassword"
            label={t("profile.password.new")}
            hint={t("profile.password.newHint")}
            required
            type={revealed ? "text" : "password"}
            autoComplete="new-password"
            value={draft.newPassword}
            error={errors.newPassword}
            onChange={onChange("newPassword")}
            trailing={
              /* Revealing beats a confirm box: re-typing a password proves only
                 that it was typed the same way twice, and seeing it is what lets
                 someone get a long one right the first time. */
              <ForgeButton
                size="sm"
                type="button"
                variant="ghost"
                onClick={onReveal}
                aria-pressed={revealed}
                aria-label={
                  revealed
                    ? t("auth:fields.password.hide")
                    : t("auth:fields.password.show")
                }
              >
                <Icon name="eye" size="sm" />
              </ForgeButton>
            }
          />
        </div>
      ) : null}
    </div>
  );
}
